import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import {
	useAuthenticatedUser,
	useFeature,
	useLocalize,
	useMixpanel,
	useViewer,
} from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Paragraph,
	Separator,
	Subheading,
	isQuillDeltaEmpty,
	tokens,
} from '@treams/treams-ui';

import useTalentReviewPath from '../../../../talent-reviews/v2/hooks/useTalentReviewPath';
import CycleMomentStatus from './CycleMomentStatus';

const CycleTitle = styled.div`
	color: ${tokens.text.secondary};
`;

const ComingSoon = styled.div`
	color: ${tokens.text.secondary};
	text-align: center;
`;

const Skip = styled(Box)`
	color: ${tokens.text.secondary};
	cursor: pointer;
	text-decoration: underline;
	font-size: 12px;

	&:hover {
		text-decoration: none;

		&:active {
			color: black;
		}
	}
`;

function CycleMomentActionButton(props: {
	showInstruction?: (options: {
		title: string;
		instruction?: graphql.LocalizedAttributedString;
		buttonTitle: string;
		callback: () => void;
	}) => void;
	template: graphql.MomentTemplate;
	onClick: () => void;
	to?: string;
	children: string;
}) {
	const { localize } = useLocalize();
	const history = useHistory();
	// The description is per language; without content in any language there is
	// nothing to show, so skip the instruction modal.
	const hasInstruction = !isQuillDeltaEmpty(localize(props.template.description ?? [])?.text);

	return (
		<>
			<Button
				small
				onClick={() => {
					if (hasInstruction && props.showInstruction) {
						props.showInstruction({
							title: localize(props.template.title).text,
							instruction: props.template.description ?? undefined,
							buttonTitle: props.children,
							callback: () => {
								props.onClick();
								if (props.to) {
									history.push(props.to);
								}
							},
						});
					} else {
						props.onClick();
						if (props.to) {
							history.push(props.to);
						}
					}
				}}
				title={props.children}
			/>
		</>
	);
}

function CycleMomentAction({
	momentInstance,
	showInstruction,
}: {
	momentInstance: graphql.MomentInstance;
	showInstruction?: (
		options:
			| {
					title: string;
					instruction?: graphql.LocalizedAttributedString;
					buttonTitle: string;
					callback: () => void;
			  }
			| undefined,
	) => void;
}) {
	const { t } = useTranslation();
	const path = useTalentReviewPath();
	const hasTalentReviewsV2 = useFeature(graphql.FeatureName.TalentReviewsV2);

	const authenticatedUser = useAuthenticatedUser();
	const mixpanel = useMixpanel();

	const isViewer = momentInstance.user.id === authenticatedUser.id;

	const [completeMomentInstance] = graphql.useCompleteMomentInstanceTaskMutation();

	if (momentInstance.evaluation) {
		return (
			<CycleMomentActionButton
				template={momentInstance.momentTemplate}
				onClick={() => mixpanel?.track('moments.evaluations.view')}
				to={`/users/${momentInstance.user.id}/conversations/${momentInstance.evaluation.id}`}
			>
				{t('inbox.cycles.moments.actions.evaluations.view')}
			</CycleMomentActionButton>
		);
	}

	if (momentInstance.momentTemplate.type === graphql.MomentType.Evaluation && isViewer) {
		return (
			<CycleMomentActionButton
				template={momentInstance.momentTemplate}
				onClick={() => mixpanel?.track('moments.evaluations.start')}
				to={`/users/${momentInstance.user.id}/conversations/request-from-moment/${momentInstance.id}`}
				showInstruction={showInstruction}
			>
				{t('inbox.cycles.moments.actions.evaluations.start')}
			</CycleMomentActionButton>
		);
	}

	if (
		momentInstance.momentTemplate.type === graphql.MomentType.Feedback &&
		momentInstance.status === graphql.MomentStatus.Open &&
		isViewer
	) {
		return (
			<CycleMomentActionButton
				template={momentInstance.momentTemplate}
				onClick={() => mixpanel?.track('moments.feedback.start')}
				to={`/users/${momentInstance.user.id}/feedback/request-from-moment/${momentInstance.id}`}
				showInstruction={showInstruction}
			>
				{t('inbox.cycles.moments.actions.feedback.start')}
			</CycleMomentActionButton>
		);
	}

	if (
		momentInstance.momentTemplate.type === graphql.MomentType.Goals &&
		momentInstance.status === graphql.MomentStatus.Open &&
		isViewer
	) {
		return (
			<CycleMomentActionButton
				template={momentInstance.momentTemplate}
				onClick={() => mixpanel?.track('moments.goals.start')}
				to={`/users/${momentInstance.user.id}/goals/new?moment_id=${momentInstance.id}`}
				showInstruction={showInstruction}
			>
				{t('inbox.cycles.moments.actions.goals.start')}
			</CycleMomentActionButton>
		);
	}

	const talentReviewGroupID = momentInstance.momentTemplate.talentReview?.group?.id;
	if (
		momentInstance.momentTemplate.type === graphql.MomentType.TalentReview &&
		momentInstance.status === graphql.MomentStatus.Open &&
		isViewer &&
		// Without a group (e.g. deleted) there is no grid to open.
		talentReviewGroupID
	) {
		return (
			<CycleMomentActionButton
				template={momentInstance.momentTemplate}
				onClick={() => mixpanel?.track('moments.talentReview.start')}
				// The v2 pages redirect to v1 without the feature, and v1 has no
				// group/template deep link, so send users to the v1 index instead.
				to={
					hasTalentReviewsV2
						? path({
								groupID: talentReviewGroupID,
								templateID: momentInstance.momentTemplate.talentReview?.template?.id,
								momentID: momentInstance.id,
							})
						: '/talent-reviews'
				}
				showInstruction={showInstruction}
			>
				{t('inbox.cycles.moments.actions.talentReview.start')}
			</CycleMomentActionButton>
		);
	}

	if (
		momentInstance.momentTemplate.type === graphql.MomentType.Message &&
		momentInstance.status === graphql.MomentStatus.Open &&
		isViewer
	) {
		return (
			<CycleMomentActionButton
				template={momentInstance.momentTemplate}
				onClick={async () => {
					mixpanel?.track('moments.message.start');
					await completeMomentInstance({
						variables: {
							input: {
								id: momentInstance.id,
							},
						},
					});
					showInstruction?.(undefined);
				}}
				showInstruction={showInstruction}
			>
				{t('inbox.cycles.moments.actions.message.start')}
			</CycleMomentActionButton>
		);
	}

	return null;
}

export default function CycleMomentPopover({
	cycle,
	instance,
	template,
	startDate,
	endDate,
	onSkip,
	onSendReminder,
	showInstruction,
}: {
	cycle: graphql.CycleTemplate;
	template: graphql.MomentTemplate;
	instance?: graphql.MomentInstance;
	startDate: Date;
	endDate: Date;
	onSkip?: () => void;
	onSendReminder?: () => void;
	showInstruction: (
		options:
			| {
					title: string;
					instruction?: graphql.LocalizedAttributedString;
					buttonTitle: string;
					callback: () => void;
			  }
			| undefined,
	) => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const viewer = useViewer();
	// Users can skip moments for other users. This component will only be shown
	// when users have full permission over the cycle.
	const skipEnabled = viewer && instance?.user.id !== viewer?.id;

	return (
		<Box p={3}>
			<Box mb={3}>
				<Flex justifyContent="space-between">
					<Subheading>
						<strong>{localize(template.title).text}</strong>
					</Subheading>
				</Flex>
				<Box mb={3}>
					<Paragraph>
						{moment.utc(startDate).format('LL')} &mdash; {moment.utc(endDate).format('LL')}
					</Paragraph>
				</Box>
				<CycleTitle>
					{t('inbox.cycles.moments.cycle.label', { title: localize(cycle.title).text })}
				</CycleTitle>

				<Box mt={3}>
					<Separator />
				</Box>
			</Box>
			{instance ? (
				<>
					{instance.status === graphql.MomentStatus.Skipped ? (
						<ComingSoon>
							{t('inbox.cycles.moments.skipped.reason')}
							<br />
							{instance.skipReason === graphql.MomentSkipReason.Other
								? instance.skipReasonOther
								: t(`inbox.cycle.moments.skipModal.reason.${instance.skipReason!.toLowerCase()}`)}
						</ComingSoon>
					) : (
						<Box mb={2}>
							<CycleMomentStatus instance={instance} />
						</Box>
					)}
					<Flex alignItems="center" flexDirection="column">
						{instance.status !== graphql.MomentStatus.Skipped && (
							<CycleMomentAction momentInstance={instance} showInstruction={showInstruction} />
						)}
						{instance.status === graphql.MomentStatus.Open && onSendReminder && (
							<Button
								title={t('inbox.cycle.moments.sendReminderButton.title')}
								small
								onClick={onSendReminder}
							/>
						)}

						{instance.status === graphql.MomentStatus.Open && skipEnabled && onSkip && (
							<Box mt={2}>
								<Skip onClick={onSkip}>{t('inbox.cycle.moments.skipButton.title')}</Skip>
							</Box>
						)}
					</Flex>
				</>
			) : (
				<ComingSoon>{t('inbox.cycles.moments.noActionRequired')}</ComingSoon>
			)}
		</Box>
	);
}
